import React, { useEffect, useState } from "react";
import {
    Container, Typography, Paper, TextField, Button,
    Table, TableHead, TableRow, TableCell, TableBody, Box
} from "@mui/material";
import { db } from "../firebase";
import { collection, getDocs, addDoc, Timestamp } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";

const Templates = () => {

    const { user } = useAuth();

    const [templates, setTemplates] = useState([]);

    const isTenantAdmin = user?.role === "appadmin";
    const isFacilityAdmin = user?.role === "facilityadmin";
    const canCreateTemplates = isTenantAdmin || isFacilityAdmin;

    const [form, setForm] = useState({
        name: "",
        description: "",
        gdriveLink: ""
    });

    const fetchTemplates = async () => {
        const snapshot = await getDocs(collection(db, "templates"));
        const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        setTemplates(data);
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const handleAddTemplate = async () => {
        if (!form.name || !form.gdriveLink) return;

        await addDoc(collection(db, "templates"), {
            name: form.name,
            description: form.description,
            gdriveLink: form.gdriveLink,
            createdBy: user.uid,
            createdAt: Timestamp.now()
        });

        setForm({ name: "", description: "", gdriveLink: "" });
        fetchTemplates();
    };

    const handleView = (link) => {
        window.open(link, "_blank");
    };

    return (
        <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>

            <Container maxWidth="lg">

                {/* HEADER */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{
                        fontSize: "2rem",
                        fontWeight: 900,
                        fontFamily: "'Unbounded', sans-serif",
                        color: "#1c1400"
                    }}>
                        Templates Management
                    </Typography>

                    <Typography sx={{ color: "#8a7a40", mt: 0.5 }}>
                        Create and manage reusable templates
                    </Typography>
                </Box>

                {/* ADD TEMPLATE */}
                {canCreateTemplates && (
                    <Paper sx={{
                        p: 4,
                        mb: 4,
                        borderRadius: "16px",
                        border: "2px solid #f0d060",
                        background: "#fff"
                    }}>
                        <Typography sx={{ fontWeight: 700, mb: 3 }}>
                            Add New Template
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                            <TextField
                                label="Template Name"
                                value={form.name}
                                fullWidth
                                inputProps={{ maxLength: 50 }}
                                helperText={`${form.name.length}/50`}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                            />

                            <TextField
                                label="Description"
                                multiline
                                rows={3}
                                value={form.description}
                                fullWidth
                                inputProps={{ maxLength: 150 }}
                                helperText={`${form.description.length}/150`}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                            />

                            <TextField
                                label="Template URL"
                                value={form.gdriveLink}
                                fullWidth
                                inputProps={{ maxLength: 250 }}
                                helperText={`${form.gdriveLink.length}/250`}
                                onChange={(e) => setForm({ ...form, gdriveLink: e.target.value })}
                            />

                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddTemplate}
                                sx={{
                                    background: "#1c1400",
                                    color: "#fde047",
                                    "&:hover": {
                                        background: "#f59e0b",
                                        color: "#1c1400"
                                    }
                                }}
                            >
                                Add Template
                            </Button>

                        </Box>
                    </Paper>
                )}

                {/* TABLE */}
                <Paper sx={{
                    borderRadius: "16px",
                    border: "2px solid #f0d060"
                }}>
                    <Box sx={{ p: 3 }}>
                        <Typography sx={{ fontWeight: 700, mb: 2 }}>
                            Templates List
                        </Typography>

                        <Table>
                            <TableHead sx={{ background: "#fef3c7" }}>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {templates.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No templates available
                                        </TableCell>
                                    </TableRow>
                                )}

                                {templates.map((t) => (
                                    <TableRow key={t.id}>
                                        <TableCell>{t.name}</TableCell>
                                        <TableCell>{t.description}</TableCell>
                                        <TableCell>
                                            <Button
                                                startIcon={<VisibilityIcon />}
                                                variant="contained"
                                                size="small"
                                                sx={{
                                                    background: "#1c1400",
                                                    color: "#fde047",
                                                    "&:hover": {
                                                        background: "#f59e0b",
                                                        color: "#1c1400"
                                                    }
                                                }}
                                                onClick={() => handleView(t.gdriveLink)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                    </Box>
                </Paper>

            </Container>
        </Box>
    );
};

export default Templates;